/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/systemVarsApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Centralized API functions for System Vars.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListSystemVarsResponse,
  CategoriesResponse,
  UpdateSystemVarPayload,
  BulkUpdateSystemVarsPayload,
  SystemVarResponse,
  GenerateXmlResponse,
} from '../types/systemVars.types';

/**
 * Fetch system vars grouped by category
 */
export async function fetchSystemVars(): Promise<ListSystemVarsResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/system-vars'), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch system vars');
  }

  return response.json();
}

/**
 * Fetch categories list
 */
export async function fetchCategories(): Promise<CategoriesResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/system-vars/categories'), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch categories');
  }

  return response.json();
}

/**
 * Update single system var
 */
export async function updateSystemVar(
  id: number,
  payload: UpdateSystemVarPayload
): Promise<SystemVarResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/system-vars/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update system var');
  }

  return response.json();
}

/**
 * Bulk update system vars
 */
export async function bulkUpdateSystemVars(
  payload: BulkUpdateSystemVarsPayload
): Promise<SystemVarResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/system-vars/bulk'), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update system vars');
  }

  return response.json();
}

/**
 * Generate vars.xml
 */
export async function generateXml(): Promise<GenerateXmlResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/system-vars/generate-xml'), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to generate XML');
  }

  return response.json();
}

/**
 * Reset all system vars to defaults
 */
export async function resetToDefaults(): Promise<SystemVarResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/system-vars/reset-to-defaults'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to reset to defaults');
  }

  return response.json();
}

